import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import type { AccountSraDto } from '../../../types/api';
import { SraRowEditor } from './SraRowEditor';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';

export interface SiteGroup {
  siteId: string;
  siteName: string;
  rows: AccountSraDto[];
}

interface SiteOption {
  siteId: string;
  siteName: string;
}

interface MasterReassignOption {
  sraId: string;
  label: string;
}

interface ManageTeamResultsListProps {
  groups: SiteGroup[];
  isLoading: boolean;
  error: string | null;
  expandedRowIds: Record<string, boolean>;
  onToggleRow: (rowId: string) => void;
  onResolveLastLogin: (row: AccountSraDto) => string;
  onResolveAccessType: (row: AccountSraDto) => string;
  onEditSite: (siteId: string, siteName: string) => void;
  onAddNewSite: (siteId: string, siteName: string) => void;
  onUpdateSra: (nextRow: AccountSraDto, options?: { reassignMasterToSraId?: string }) => Promise<void>;
  siteOptions: SiteOption[];
  currentLoggedInSraId?: string;
  currentLoggedInSraEmail?: string;
  masterRowId?: string | null;
  canReassignMaster?: boolean;
  masterReassignCandidates?: MasterReassignOption[];
}

const getRowId = (row: AccountSraDto): string => {
  return row.SraId || `${row.SiteId}:${row.Email}`;
};

export const ManageTeamResultsList: React.FC<ManageTeamResultsListProps> = ({
  groups,
  isLoading,
  error,
  expandedRowIds,
  onToggleRow,
  onResolveLastLogin,
  onResolveAccessType,
  onEditSite,
  onAddNewSite,
  onUpdateSra,
  siteOptions,
  currentLoggedInSraId,
  currentLoggedInSraEmail,
  masterRowId,
  canReassignMaster,
  masterReassignCandidates = [],
}) => {
  if (isLoading) {
    return (
      <View style={styles.centeredState}>
        <ActivityIndicator size="large" color={EDITOR_COLORS.accent} />
        <Text style={styles.loadingText}>Loading team access...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorBox}>
        <Text style={styles.errorTitle}>Could not load team access</Text>
        <Text style={styles.errorMsg}>{error}</Text>
      </View>
    );
  }

  if (groups.length === 0) {
    return (
      <View style={styles.centeredState}>
        <Text style={styles.emptyText}>No team members match the current filters.</Text>
      </View>
    );
  }

  return (
    <View style={styles.listWrap}>
      {groups.map((group) => (
        <View key={group.siteId} style={styles.siteGroupBlock}>
          <View style={styles.siteHeaderRow}>
            <View style={styles.siteHeaderLeft}>
              <Text style={styles.siteHeading}>Site - {group.siteName}</Text>
              <Pressable
                onPress={() => onEditSite(group.siteId, group.siteName)}
                style={styles.siteActionBtn}
                accessibilityRole="button"
                accessibilityLabel={`Edit site ${group.siteName}`}
              >
                <Text style={styles.siteActionBtnText}>Edit</Text>
              </Pressable>
            </View>

            {groups[0]?.siteId === group.siteId ? (
              <Pressable
                onPress={() => onAddNewSite(group.siteId, group.siteName)}
                style={styles.siteActionBtn2}
                accessibilityRole="button"
                accessibilityLabel="Add new site"
              >
                <Text style={styles.siteActionBtnText}>Add New Site</Text>
              </Pressable>
            ) : null}
          </View>

          <View style={styles.headerRow}>
            <View style={styles.toggleCol}>
              <Text style={styles.headerText}></Text>
            </View>
            <View style={styles.nameCol}>
              <Text style={styles.headerText}>Name</Text>
            </View>
            <View style={styles.emailCol}>
              <Text style={styles.headerText}>Email</Text>
            </View>
            <View style={styles.lastLoginCol}>
              <Text style={styles.headerText}>Last Login</Text>
            </View>
            <View style={styles.accessTypeCol}>
              <Text style={styles.headerText}>Access Type</Text>
            </View>
          </View>

          {group.rows.map((row) => {
            const rowId = getRowId(row);
            return (
              <SraRowEditor
                key={rowId}
                row={row}
                expanded={!!expandedRowIds[rowId]}
                lastLogin={onResolveLastLogin(row)}
                accessTypeLabel={onResolveAccessType(row)}
                onToggle={() => onToggleRow(rowId)}
                onUpdateSra={onUpdateSra}
                siteOptions={siteOptions}
                currentLoggedInSraId={currentLoggedInSraId}
                currentLoggedInSraEmail={currentLoggedInSraEmail}
                isMasterAccount={!!masterRowId && masterRowId === rowId}
                canReassignMaster={!!canReassignMaster}
                masterReassignCandidates={masterReassignCandidates}
              />
            );
          })}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  listWrap: {
    width: '100%',
    maxWidth: 850,
    alignSelf: 'center',
    gap: 14,
  },
  centeredState: {
    paddingVertical: 38,
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  emptyText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
  errorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorMsg: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 19,
  },
  siteGroupBlock: {
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: EDITOR_COLORS.background,
  },
  siteHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  siteHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  siteHeading: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  siteActionBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  siteActionBtn2: {
    width: 125,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  siteActionBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  headerRow: {
    minHeight: 38,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    backgroundColor: EDITOR_COLORS.background,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'left',
  },
  toggleCol: {
    width: '8%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 8,
  },
  nameCol: {
    width: '24%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  emailCol: {
    width: '32%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  lastLoginCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  accessTypeCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
});
